import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Clear as ClearIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/cjs/styles/prism';

import { apiService, apiUtils } from '../services/api';
import { useApp } from '../context/AppContext';

const codeExamples = {
  python: `# Python Example - System Information
import platform
import datetime

print("🐍 Python Sandbox Environment")
print(f"Platform: {platform.system()} {platform.release()}")
print(f"Python Version: {platform.python_version()}")
print(f"Current Time: {datetime.datetime.now()}")

# Simple calculation
numbers = [1, 2, 3, 4, 5]
result = sum(x**2 for x in numbers)
print(f"Sum of squares: {result}")`,

  javascript: `// JavaScript Example - Async Operations
// JavaScript Sandbox Environment
// Node.js Version: v18.x.x
// Platform: linux

// Async function example
async function fetchData() {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve({ message: "Hello from async function!", timestamp: new Date() });
    }, 1000);
  });
}

// Execute async operation
fetchData().then(data => {
  // Process async result
  return data;
});

// Array operations
const numbers = [1, 2, 3, 4, 5];
const doubled = numbers.map(x => x * 2);
// Original: [1, 2, 3, 4, 5]
// Doubled: [2, 4, 6, 8, 10]`,

  bash: `#!/bin/bash
# Bash Example - System Commands
echo "🐚 Bash Sandbox Environment"
echo "Current Date: $(date)"
echo "System Info: $(uname -a)"
echo "Current Directory: $(pwd)"

# File operations
echo "Creating temporary files..."
echo "Hello World" > /tmp/test.txt
echo "File content: $(cat /tmp/test.txt)"

# Process information
echo "Current processes:"
ps aux | head -5

echo "PATH: $PATH"
echo "Shell: $SHELL"`
};

const Sandbox = () => {
  const { t } = useTranslation();
  const { websocketConnected } = useApp();
  const [code, setCode] = useState(codeExamples.python);
  const [language, setLanguage] = useState('python');
  const [environment, setEnvironment] = useState('default');
  const [timeout, setTimeout] = useState(30);
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [executing, setExecuting] = useState(false);
  const [executionTime, setExecutionTime] = useState(0);
  const [environments, setEnvironments] = useState([]);
  const [sandboxStatus, setSandboxStatus] = useState(null);

  useEffect(() => {
    loadEnvironments();
    loadSandboxStatus();
  }, []);

  useEffect(() => {
    // Update code example when language changes
    if (codeExamples[language]) {
      setCode(codeExamples[language]);
    }
  }, [language]);

  const loadEnvironments = async () => {
    try {
      const envs = await apiService.getSandboxEnvironments();
      setEnvironments(envs);
    } catch (err) {
      // Silent error handling
    }
  };

  const loadSandboxStatus = async () => {
    try {
      const status = await apiService.getSandboxStatus();
      setSandboxStatus(status);
    } catch (err) {
      // Silent error handling
    }
  };

  const executeCode = async () => {
    // Validate input
    const validationErrors = apiUtils.validateCodeRequest({
      code,
      language,
      timeout,
      environment,
    });

    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    setExecuting(true);
    setOutput('');
    setError('');
    setExecutionTime(0);

    const startTime = Date.now();

    try {
      const result = await apiService.executeCode({
        code,
        language,
        timeout,
        environment,
      });

      setExecutionTime((Date.now() - startTime) / 1000);
      if (result.status === 'completed') {
        setOutput(result.output || t('code_executed_successfully_no_output'));
        if (result.error) {
          setError(result.error);
        }
      } else {
        setError(result.error || t('execution_failed'));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setExecuting(false);
    }
  };

  const clearOutput = () => {
    setOutput('');
    setError('');
    setExecutionTime(0);
  };

  const copyOutput = () => {
    navigator.clipboard.writeText(output);
  };

  const downloadOutput = () => {
    const blob = new Blob([output], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sandbox-output-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          {t('code_sandbox')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('interactive_code_execution')}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Code Editor */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">{t('code_sandbox')}</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>{t('language')}</InputLabel>
                    <Select
                      value={language}
                      label={t('language')}
                      onChange={(e) => setLanguage(e.target.value)}
                    >
                      <MenuItem value="python">{t('python')}</MenuItem>
                      <MenuItem value="javascript">{t('javascript')}</MenuItem>
                      <MenuItem value="bash">{t('bash')}</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>{t('environment')}</InputLabel>
                    <Select
                      value={environment}
                      label={t('environment')}
                      onChange={(e) => setEnvironment(e.target.value)}
                    >
                      <MenuItem value="default">
                        {t('standard_development_environment')}
                      </MenuItem>
                      {environments.map((env) => (
                        <MenuItem key={env.name} value={env.name}>
                          {env.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              <TextField
                  fullWidth
                  multiline
                  rows={12}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder={t('enter_code_here')}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                      fontSize: '14px',
                    },
                  }}
                />

                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={executing ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                    onClick={executeCode}
                    disabled={executing || !code.trim()}
                  >
                    {executing ? t('running_code') : t('run_code')}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={() => setCode('')}
                  >
                    {t('clear')}
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Output */}
            <Card>
              <CardContent>
                <Typography variant="h6">{t('output')}</Typography>
                <Paper
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: '#1e1e1e',
                    color: '#ffffff',
                    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                    fontSize: '14px',
                    minHeight: '200px',
                    maxHeight: '400px',
                    overflow: 'auto',
                  }}
                >
                  {output ? (
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{output}</pre>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontStyle: 'italic' }}
                    >
                      {t('output_will_appear')}
                    </Typography>
                  )}
                </Paper>
              </CardContent>
            </Card>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} lg={4}>
            {/* Environment Info */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('environment_info')}
                </Typography>
                
                {environments.find(env => env.name === environment) && (
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {t('standard_development_environment')}
                    </Typography>
                    
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>{t('supported_languages')}:</strong>
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      <Chip label="python" size="small" />
                      <Chip label="javascript" size="small" />
                      <Chip label="bash" size="small" />
                    </Box>
                    
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>{t('memory_limit')}:</strong> 512MB
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>{t('cpu_limit')}:</strong> 1 core
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('sandbox_timeout')}:</strong> 30s
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Sandbox Status */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('sandbox_status')}
                </Typography>
                
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>{t('sandbox_status_label')}:</strong>
                  <Chip 
                    label={t('running')}
                    color="success" 
                    size="small" 
                    sx={{ ml: 1 }}
                  />
                </Typography>
                
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>{t('active_executions')}:</strong> 0
                </Typography>
                <Typography variant="body2">
                  <strong>{t('total_executions')}:</strong> 0
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
  );
};

export default Sandbox;
